'use client';

import { getOrCreateAnonId, getOrCreateSessionId } from './tracking/identity';

interface TrackEvent {
  targetType: 'post' | 'user' | 'channel' | 'tag' | 'vitals' | 'journey' | 'attribution' | 'message';
  targetId: string;
  actionType: 'view' | 'click' | 'dwell' | 'like' | 'repost' | 'share' | 'follow_post' | 'comment' | 'follow'
    | 'page_view' | 'sign_up' | 'first_post' | 'agent_search' | 'agent_view' | 'subscribe'
    | 'payment_approve' | 'payment_deny'
    // Frontend mirrors of server-side message events. trackChatSend/trackChatRate
    // exist for flows that bypass /api/messages (e.g. optimistic UI), and
    // trackContentDownload has no server emit at all (download is a CDN GET).
    | 'chat_send' | 'message_rate' | 'content_download'
    // Funnel + reliability events (one-PR build): first real edit in a widget
    // (C0b bridge emit) and swallowed guest-mint failures (D5).
    | 'content_action' | 'first_meaningful_edit' | 'guest_mint_failed'
    | 'first' | 'last' | 'signup' // attribution touch types
    | 'nav_click' | 'cohort_assigned' // splash / funnel UI events (journey rows)
    | 'web_vital';
  metadata?: {
    dwellTime?: number;
    scrollDepth?: number;
    source?: string;
    vitalName?: string;
    vitalValue?: number;
    vitalRating?: string;
    path?: string;
    referrer?: string;
    method?: string;
    query?: string;
    agentId?: string;
    amount?: number;
    // Attribution fields (set by ReferralCapture)
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    landingPath?: string;
    refCode?: string;
    adClick?: { param: string; value: string } | null;
    [k: string]: unknown;
  };
}

interface TrackPayload {
  events: TrackEvent[];
  // Identity is also injected per-event for /api/track endpoints that read top-level
  anonId: string | null;
  sessionId: string | null;
}

class EventTracker {
  private queue: TrackEvent[] = [];
  private flushTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private viewedPosts = new Set<string>(); // Track already-viewed posts to avoid duplicates
  
  constructor() {
    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flush());
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    }
  }

  track(event: TrackEvent) {
    // Dedupe view events within session
    if (event.actionType === 'view') {
      const key = `${event.targetType}:${event.targetId}`;
      if (this.viewedPosts.has(key)) {
        return;
      }
      this.viewedPosts.add(key);
    }

    this.queue.push(event);
    
    // Flush if batch size reached
    if (this.queue.length >= this.BATCH_SIZE) {
      this.flush();
    } else {
      // Schedule flush
      this.scheduleFlush();
    }
  }

  private scheduleFlush() {
    if (this.flushTimeout) {
      return;
    }
    
    this.flushTimeout = setTimeout(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);
  }

  async flush() {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }

    if (this.queue.length === 0) {
      return;
    }

    const events = [...this.queue];
    this.queue = [];

    // sendBeacon cannot carry custom headers, so identity ships in the
    // top-level payload and `/api/track` reads it from there.
    const payload: TrackPayload = {
      events,
      anonId: getOrCreateAnonId(),
      sessionId: getOrCreateSessionId(),
    };

    try {
      const body = JSON.stringify(payload);
      const blob = new Blob([body], { type: 'application/json' });
      const beaconOk = typeof navigator !== 'undefined'
        && navigator.sendBeacon
        && navigator.sendBeacon('/api/track', blob);

      if (!beaconOk) {
        await fetch('/api/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
      }
    } catch (error) {
      this.queue.unshift(...events);
      console.error('Failed to track events:', error);
    }
  }

  // Convenience methods
  trackView(targetType: TrackEvent['targetType'], targetId: string, source?: string) {
    this.track({
      targetType,
      targetId,
      actionType: 'view',
      metadata: source ? { source } : undefined,
    });
  }

  trackClick(targetType: TrackEvent['targetType'], targetId: string) {
    this.track({
      targetType,
      targetId,
      actionType: 'click',
    });
  }

  trackDwell(targetType: TrackEvent['targetType'], targetId: string, dwellTime: number) {
    // Only track if dwell time is significant (>5 seconds)
    if (dwellTime < 5000) {
      return;
    }
    
    this.track({
      targetType,
      targetId,
      actionType: 'dwell',
      metadata: { dwellTime },
    });
  }

  trackLike(targetType: TrackEvent['targetType'], targetId: string) {
    this.track({
      targetType,
      targetId,
      actionType: 'like',
    });
  }

  trackRepost(targetId: string) {
    this.track({
      targetType: 'post',
      targetId,
      actionType: 'repost',
    });
  }

  trackShare(targetType: TrackEvent['targetType'], targetId: string) {
    this.track({
      targetType,
      targetId,
      actionType: 'share',
    });
  }

  trackFollowPost(targetType: TrackEvent['targetType'], targetId: string) {
    this.track({
      targetType,
      targetId,
      actionType: 'follow_post',
    });
  }

  trackFollow(targetType: 'user' | 'channel' | 'tag', targetId: string) {
    this.track({
      targetType,
      targetId,
      actionType: 'follow',
    });
  }

  // ── Journey methods ──────────────────────────────────────────────────

  trackPageView(path: string, referrer?: string) {
    this.track({
      targetType: 'journey',
      targetId: path,
      actionType: 'page_view',
      metadata: { path, referrer },
    });
  }

  trackSignUp(method: string) {
    this.track({ targetType: 'journey', targetId: 'sign_up', actionType: 'sign_up', metadata: { method } });
  }

  trackFirstPost() {
    this.track({ targetType: 'journey', targetId: 'first_post', actionType: 'first_post' });
  }

  trackAgentSearch(query: string) {
    this.track({ targetType: 'journey', targetId: 'agent_search', actionType: 'agent_search', metadata: { query } });
  }

  trackAgentView(agentId: string) {
    this.track({ targetType: 'journey', targetId: agentId, actionType: 'agent_view', metadata: { agentId } });
  }

  trackSubscribe(agentId: string) {
    this.track({ targetType: 'journey', targetId: agentId, actionType: 'subscribe', metadata: { agentId } });
  }

  trackPaymentApprove(agentId: string, amount: number) {
    this.track({ targetType: 'journey', targetId: agentId, actionType: 'payment_approve', metadata: { agentId, amount } });
  }

  trackPaymentDeny(agentId: string) {
    this.track({ targetType: 'journey', targetId: agentId, actionType: 'payment_deny', metadata: { agentId } });
  }

  // ── Chat / content (mirrors of server emits) ─────────────────────────
  // These are FE-side mirrors so flows that don't hit /api/messages
  // (optimistic UI, WS-only client paths) can still emit a chat_send /
  // message_rate row. Server emits remain authoritative for routes that
  // pass through /api/messages, /api/messages/[id]/rate, /api/content/upload.
  // To avoid double-counting the post-route hop, only call these from
  // surfaces that DON'T hit the corresponding API route.

  trackChatSend(messageId: string, metadata?: { chatId?: string; contentLength?: number; hasMedia?: boolean }) {
    this.track({
      targetType: 'message',
      targetId: messageId,
      actionType: 'chat_send',
      metadata,
    });
  }

  trackChatRate(messageId: string, rating: -1 | 1, metadata?: { chatId?: string; agentId?: string }) {
    this.track({
      targetType: 'message',
      targetId: messageId,
      actionType: 'message_rate',
      metadata: { rating, ...metadata },
    });
  }

  trackContentDownload(contentId: string, metadata?: { contentType?: string; sizeBytes?: number }) {
    this.track({
      targetType: 'message',
      targetId: contentId,
      actionType: 'content_download',
      metadata,
    });
  }

  // ── Attribution touches ──────────────────────────────────────────────

  /**
   * Record an attribution touch (first / last / signup). targetId is the
   * inferred source ('twitter', 'direct', '<utm_source>'). metadata carries
   * the full UTM bag + referrer + landing path for downstream reports.
   */
  trackAttribution(
    actionType: 'first' | 'last' | 'signup',
    targetId: string,
    metadata: TrackEvent['metadata'],
  ) {
    this.track({ targetType: 'attribution', targetId, actionType, metadata });
  }

  // ── Web Vitals ───────────────────────────────────────────────────────

  trackWebVital(name: string, value: number, rating: string) {
    this.track({
      targetType: 'vitals',
      targetId: name,
      actionType: 'web_vital',
      metadata: { vitalName: name, vitalValue: value, vitalRating: rating },
    });
  }
}

// Singleton instance
export const tracker = typeof window !== 'undefined' ? new EventTracker() : null;

/**
 * Install global error handlers that report to /api/errors.
 * Uses sendBeacon for reliability during page unload.
 *
 * Coalesces identical errors (same stack within 60s) to defend against
 * a buggy render loop spamming thousands of rows/min. The server-side
 * rate-limit on /api/errors is the second line of defence.
 */
export function installErrorHandlers() {
  if (typeof window === 'undefined') return;

  const seen = new Map<string, number>(); // stackKey → last-emit timestamp
  const COALESCE_MS = 60_000;

  function quickHash(s: string): string {
    // Tiny FNV-1a — exact value doesn't matter, only that identical
    // strings collide and different ones (almost always) don't.
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h.toString(16);
  }

  function shouldEmit(key: string): boolean {
    const now = Date.now();
    const last = seen.get(key) ?? 0;
    if (now - last < COALESCE_MS) return false;
    seen.set(key, now);
    // Trim opportunistically so the map can't grow unbounded.
    if (seen.size > 256) {
      for (const [k, t] of seen) {
        if (now - t > COALESCE_MS) seen.delete(k);
      }
    }
    return true;
  }

  function report(payload: Record<string, unknown>) {
    try {
      const enriched = {
        ...payload,
        anonId: getOrCreateAnonId(),
        sessionId: getOrCreateSessionId(),
      };
      const body = JSON.stringify(enriched);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          '/api/errors',
          new Blob([body], { type: 'application/json' }),
        );
      } else {
        fetch('/api/errors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        }).catch(() => {});
      }
    } catch {
      // Must never throw
    }
  }

  window.addEventListener('error', (event) => {
    const stack = event.error?.stack ?? '';
    const key = quickHash(stack || `${event.message}:${event.filename}:${event.lineno}`);
    if (!shouldEmit(key)) return;
    report({
      type: 'js_error',
      message: event.message,
      stack,
      url: event.filename,
      line: event.lineno,
      col: event.colno,
      userAgent: navigator.userAgent,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const stack = reason instanceof Error ? (reason.stack ?? '') : '';
    const message = reason instanceof Error ? reason.message : String(reason);
    const key = quickHash(stack || message);
    if (!shouldEmit(key)) return;
    report({
      type: 'promise_rejection',
      message,
      stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  });
}

export function reportWebVitals() {
  if (typeof window === 'undefined' || !tracker) return;
  import('web-vitals').then((vitals) => {
    const report = (metric: { name: string; value: number; rating: string }) => {
      tracker!.trackWebVital(metric.name, metric.value, metric.rating);
    };
    vitals.onCLS(report);
    vitals.onFCP(report);
    vitals.onLCP(report);
    vitals.onTTFB(report);
    vitals.onINP(report);
    // onFID removed in web-vitals v4+; INP supersedes it
    if ('onFID' in vitals && typeof vitals.onFID === 'function') {
      (vitals as { onFID: typeof vitals.onCLS }).onFID(report);
    }
  });
}

// React hook for tracking post views with IntersectionObserver
export function usePostViewTracking(postId: string, enabled = true) {
  if (typeof window === 'undefined' || !enabled || !tracker) {
    return { ref: null };
  }

  const ref = (node: HTMLElement | null) => {
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            tracker.trackView('post', postId, 'feed');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 } // 50% visible
    );

    observer.observe(node);
  };

  return { ref };
}

// Hook for tracking dwell time
export function useDwellTracking(targetType: TrackEvent['targetType'], targetId: string, enabled = true) {
  if (typeof window === 'undefined' || !enabled || !tracker) {
    return;
  }

  const startTime = Date.now();

  return () => {
    const dwellTime = Date.now() - startTime;
    tracker.trackDwell(targetType, targetId, dwellTime);
  };
}
